import React, { useState, useEffect } from 'react';
import { getCieCodes, createCieCode, updateCieCode, deleteCieCode } from '../../servicios/apiClinica';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const CieCodes = () => {
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState(null);
    
    // States for Modal and CRUD
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedCode, setSelectedCode] = useState(null);
    const [formData, setFormData] = useState({ code: '', description: '' });

    useEffect(() => {
        loadCodes();
    }, []);

    const loadCodes = async () => {
        setLoading(true);
        try {
            const data = await getCieCodes();
            setCodes(data);
        } catch (error) {
            console.error(error);
            setMessage({ type: 'danger', text: 'Error al cargar códigos.' });
        }
        setLoading(false);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        setMessage(null);

        const formDataFile = new FormData();
        formDataFile.append('file', file);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/clinical-records/cie-codes/import_excel/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataFile
            });

            const result = await response.json();
            if (response.ok) {
                setMessage({ type: 'success', text: result.message || 'Códigos importados correctamente.' });
                loadCodes();
            } else {
                setMessage({ type: 'danger', text: result.error || 'Error al importar.' });
            }
        } catch (error) {
            console.error(error);
            setMessage({ type: 'danger', text: 'Error de conexión.' });
        }
        setUploading(false);
        e.target.value = ''; // Reset input
    };

    const handleOpenModal = (mode, codeData = null) => {
        setModalMode(mode);
        setSelectedCode(codeData);
        setFormData(codeData ? { ...codeData } : { code: '', description: '' });
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (modalMode === 'create') {
                await createCieCode(formData);
                setMessage({ type: 'success', text: 'Código creado correctamente.' });
            } else {
                await updateCieCode(selectedCode.code, formData);
                setMessage({ type: 'success', text: 'Código actualizado correctamente.' });
            }
            setShowModal(false);
            loadCodes();
        } catch (error) {
            setMessage({ type: 'danger', text: 'Error al guardar el código.' });
        }
    };

    const handleDelete = async (codeStr) => {
        if (window.confirm(`¿Estás seguro de eliminar el código ${codeStr}?`)) {
            try {
                await deleteCieCode(codeStr);
                setMessage({ type: 'success', text: 'Código eliminado correctamente.' });
                loadCodes();
            } catch (error) {
                setMessage({ type: 'danger', text: 'Error al eliminar el código.' });
            }
        }
    };

    return (
        <div className="container-fluid p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex flex-column">
                    <h2 style={{ color: '#0056b3', fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '4px' }}>
                        <i className="bi bi-file-medical-fill me-2" style={{ color: '#0056b3' }}></i> 
                        Administrador de Códigos CIE-10
                    </h2>
                    <p className="text-muted mb-0" style={{ fontSize: '1rem', fontWeight: '500' }}>
                        Gestiona y actualiza la base de datos de diagnósticos.
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <label htmlFor="file-upload" className="btn btn-outline-primary d-flex align-items-center" style={{ borderRadius: '8px', fontWeight: 'bold' }}>
                        {uploading ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : (
                            <i className="bi bi-file-earmark-excel-fill me-2"></i>
                        )}
                        Subir Excel
                    </label>
                    <input 
                        id="file-upload"
                        type="file" 
                        accept=".xlsx, .xls" 
                        className="d-none" 
                        onChange={handleFileUpload}
                        disabled={uploading}
                    />
                    <button 
                        onClick={() => handleOpenModal('create')}
                        className="btn shadow-sm" 
                        style={{ backgroundColor: '#28a745', color: 'white', fontWeight: 'bold', borderRadius: '8px' }}
                    >
                        <i className="bi bi-plus-lg me-2"></i> Nuevo Código
                    </button>
                </div>
            </div>
            
            {message && (
                <div className={`alert alert-${message.type} alert-dismissible fade show shadow-sm`} role="alert" style={{ borderRadius: '10px' }}>
                    <i className={`bi ${message.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                    {message.text}
                    <button type="button" className="btn-close" onClick={() => setMessage(null)} aria-label="Close"></button>
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold m-0" style={{ color: '#333' }}>
                    <i className="bi bi-list-ul me-2 text-primary"></i> 
                    Códigos Actuales Registrados
                </h4>
                <span className="badge bg-primary rounded-pill px-3 py-2" style={{ fontSize: '1rem' }}>
                    Total: {codes.length}
                </span>
            </div>
            
            <div className="card shadow-sm border-0" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}></div>
                            <p className="mt-3 text-muted fw-bold">Cargando base de datos CIE-10...</p>
                        </div>
                    ) : (
                        <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                            <table className="table table-hover mb-0">
                                <thead style={{ backgroundColor: '#0056b3', color: 'white', position: 'sticky', top: 0, zIndex: 1 }}>
                                    <tr>
                                        <th className="py-3 px-4 border-0 text-center" style={{ width: '15%' }}>Código CIE-10</th>
                                        <th className="py-3 px-4 border-0">Descripción del Diagnóstico</th>
                                        <th className="py-3 px-4 border-0 text-center" style={{ width: '15%' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {codes.length > 0 ? (
                                        codes.map(c => (
                                            <tr key={c.code} style={{ transition: 'background-color 0.2s' }}>
                                                <td className="py-3 px-4 text-center">
                                                    <span className="badge" style={{ backgroundColor: '#e2f0d9', color: '#385623', fontSize: '0.9rem', padding: '6px 12px' }}>
                                                        {c.code}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 align-middle" style={{ fontWeight: '500', color: '#555' }}>
                                                    {c.description}
                                                </td>
                                                <td className="py-3 px-4 text-center">
                                                    <button onClick={() => handleOpenModal('edit', c)} className="btn btn-sm btn-outline-warning rounded-circle me-2" title="Editar">
                                                        <i className="bi bi-pencil-fill"></i>
                                                    </button>
                                                    <button onClick={() => handleDelete(c.code)} className="btn btn-sm btn-outline-danger rounded-circle" title="Eliminar">
                                                        <i className="bi bi-trash-fill"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="text-center py-5 text-muted">
                                                <i className="bi bi-inbox fs-1 d-block mb-3 opacity-50"></i>
                                                <h5>No hay códigos cargados en el sistema</h5>
                                                <p>Utiliza la opción de importar archivo Excel arriba para cargar la base de datos.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal CRUD */}
            {showModal && (
                <div 
                    className="modal show d-block" 
                    style={{ 
                        position: 'fixed', 
                        top: 0, 
                        left: 0, 
                        width: '100vw', 
                        height: '100vh', 
                        backgroundColor: 'rgba(0,0,0,0.6)', 
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: 0,
                        padding: 0
                    }}
                >
                    <div className="modal-dialog w-100" style={{ maxWidth: '600px', margin: '1.75rem auto' }}>
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="modal-header border-0 p-4" style={{ backgroundColor: '#002b5e' }}>
                                <div className="w-100">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <h4 className="modal-title fw-bold m-0" style={{ color: '#ffffff', letterSpacing: '0.5px' }}>
                                            <i className={`bi ${modalMode === 'create' ? 'bi-plus-circle' : 'bi-pencil'} me-2`}></i>
                                            {modalMode === 'create' ? 'Nuevo Código CIE-10' : 'Editar Código CIE-10'}
                                        </h4>
                                        <button type="button" onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', color: '#ffffff', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                                    </div>
                                    <span style={{ color: '#e0e0e0', fontSize: '0.95rem' }}>Complete cuidadosamente la información del diagnóstico.</span>
                                </div>
                            </div>
                            
                            <div className="modal-body p-4 bg-light">
                                <form id="cie-form" onSubmit={handleSave}>
                                    <div className="card border-0 shadow-sm" style={{ borderRadius: '12px', backgroundColor: '#ffffff' }}>
                                        <div className="card-body p-4">
                                            <h6 className="fw-bold mb-4 pb-2" style={{ color: '#002b5e', borderBottom: '2px solid #f0f0f0' }}>
                                                <i className="bi bi-file-earmark-medical-fill me-2" style={{ color: '#b6005b' }}></i>
                                                Datos Principales del Diagnóstico
                                            </h6>
                                            <div className="mb-4">
                                                <label className="fw-bold text-dark mb-2" style={{ fontSize: '0.95rem' }}>Código CIE-10 <span className="text-danger">*</span></label>
                                                <input 
                                                    type="text" 
                                                    className="form-control form-control-lg" 
                                                    value={formData.code}
                                                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                                                    required
                                                    disabled={modalMode === 'edit'} 
                                                    placeholder="Ej. K021"
                                                    style={{ fontSize: '1rem', backgroundColor: modalMode === 'edit' ? '#f8f9fa' : '#ffffff' }}
                                                />
                                                {modalMode === 'edit' && <small className="text-muted mt-1 d-block"><i className="bi bi-info-circle me-1"></i>El código no se puede modificar una vez creado.</small>}
                                            </div>
                                            <div>
                                                <label className="fw-bold text-dark mb-2" style={{ fontSize: '0.95rem' }}>Descripción del Diagnóstico <span className="text-danger">*</span></label>
                                                <textarea 
                                                    className="form-control" 
                                                    rows="4"
                                                    value={formData.description}
                                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                                    required
                                                    placeholder="Escriba la descripción detallada aquí..."
                                                    style={{ resize: 'none', fontSize: '1rem' }}
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="modal-footer bg-light border-0 p-4 pt-0 d-flex justify-content-end gap-3">
                                <button type="button" className="btn btn-outline-secondary fw-bold px-4 py-2" onClick={() => setShowModal(false)} style={{ borderRadius: '8px' }}>
                                    Cancelar
                                </button>
                                <button type="submit" form="cie-form" className="btn fw-bold px-4 py-2" style={{ backgroundColor: '#b6005b', color: 'white', borderRadius: '8px', boxShadow: '0 4px 6px rgba(182, 0, 91, 0.2)' }}>
                                    <i className="bi bi-save-fill me-2"></i>Guardar Cambios
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CieCodes;

